// ChartRenderer.jsx - Updated to handle route-specific charts
import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import BarGraph from "./charts/BarGraph";
import LineGraph from "./charts/LineGraph";
import PieChart from "./charts/PieChart";
import { ChartSkeleton, MultiBarChartSkeleton } from "./GraphSkelton";
import {
  mapReportDataToChartData,
  mapTripDataForMultiVehicleChart,
  mapTripDataForGanttChart,
} from "./dataMapping";

import {
  calculateTotalForChart,
  mapOverspeedDataForMultiVehicleChart,
} from "./dataUtils";
import { getAxisLabels } from "./dataUtils";
import HorizontalBarTimeline from "./charts/HorizontalBarTimeline";
import LoadingIndicator from "./LoadingIndicator";
import config from "../../../config.json";

const ChartRenderer = ({
  chartConfig,
  chartState,
  vehiclesData,
  selectedDateRange,
  loadingState, // Add this prop
}) => {
  const { t } = useTranslation();
  const [maxSpeedLimit, setMaxSpeedLimit] = useState(null);

  useEffect(() => {
    const fetchMaxSpeed = async () => {
      if (chartConfig.id === "maximum_speed") {
        try {
          const orgId = localStorage.getItem("userState")
            ? JSON.parse(localStorage.getItem("userState")).organization.id
            : null;
          // console.log("Organization ID:", orgId);
          const response = await fetch(
            `${config.baseURL}/report/overspeed?organizationId=${orgId}&dateOption=today&allVehicles=true&allRoutes=false&allDrivers=false&allStops=false&allSites=false&page=1&pageSize=1`
          );
          const data = await response.json();
          setMaxSpeedLimit(data.maxSpeed);
        } catch (error) {
          console.error("Error fetching max speed:", error);
        }
      }
    };

    fetchMaxSpeed();
  }, [chartConfig.id]);

  if (chartState.loading) {
    if (
      chartConfig.id === "total_halt" ||
      chartConfig.id === "halt_duration" ||
      chartConfig.id === "total_trips" ||
      chartConfig.id.startsWith("route_specific_trips")
    ) {
      return <MultiBarChartSkeleton />;
    }
    return (
      <ChartSkeleton
        chartType={chartConfig.chartType}
        title={chartConfig.title}
      />
    );
  }

  if (chartState.error) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="error">
          Error loading {t(chartConfig.title)}: {chartState.error}
        </Typography>
      </Box>
    );
  }

  if (!chartState.data?.success) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="warning.main">
          No data available for {t(chartConfig.title)}
        </Typography>
      </Box>
    );
  }

  // Special handling for route-specific charts
  if (chartConfig.id.startsWith("route_specific_trips")) {
    const transformedData = mapReportDataToChartData(
      chartState.data,
      {
        ...chartConfig,
        title: "Route Specific Trips by Vehicle",
      },
      vehiclesData
    );

    if (!transformedData || transformedData.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            No trips found for {chartConfig.routeName}
          </Typography>
        </Box>
      );
    }

    const total = transformedData.reduce(
      (sum, item) => sum + (item.total || 0),
      0
    );
    const displayTitle = `${chartConfig.routeName} Trips: ${total}`;

    return (
      <BarGraph
        data={transformedData}
        title={displayTitle}
        width={chartConfig.width}
        height={chartConfig.height}
        color={chartConfig.color}
        xAxis="vehicle"
        xAxisLabel="Vehicles"
        yAxisLabel="Trips"
        showLegend={true}
        completedKey="onTime"
        delayedKey="delayed"
        completedColor="#28a745"
        delayedColor="#ffc107"
        barSize={14}
      />
    );
  }

  // Special handling for overspeed timeline chart
  if (chartConfig.id === "overspeed_timeline") {
    const overspeedData = mapOverspeedDataForMultiVehicleChart(chartState.data);

    if (!overspeedData || overspeedData.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            No overspeed data to display
          </Typography>
        </Box>
      );
    }

    // Generate vehicle colors
    const uniqueVehicles = [
      ...new Set(overspeedData.map((item) => item.vehicleName)),
    ];
    const vehicleColors = {};
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];

    uniqueVehicles.forEach((vehicle, index) => {
      vehicleColors[vehicle] = colors[index % colors.length];
    });

    return (
      <LineGraph
        data={overspeedData}
        title={`${t(chartConfig.title)} - Speed Over Time`}
        width={chartConfig.width}
        height={chartConfig.height}
        isMultiVehicle={true}
        vehicleColors={vehicleColors}
        timeField="time"
        speedField="speed"
        vehicleField="vehicleName"
        showDots={true}
        showLegend={false}
      />
    );
  }

  if (chartConfig.id === "trip_timeline") {
    // Use average duration over time approach
    const tripTimelineData = mapTripDataForMultiVehicleChart(
      chartState.data,
      "average_duration"
    );

    if (!tripTimelineData || tripTimelineData.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            No trip timeline data to display
          </Typography>
        </Box>
      );
    }

    // Get all vehicle names from the timeline data
    const allVehicles = new Set();
    tripTimelineData.forEach((timeSlot) => {
      Object.keys(timeSlot).forEach((key) => {
        if (key !== "time") {
          allVehicles.add(key);
        }
      });
    });

    const vehicleColors = {};
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];

    Array.from(allVehicles).forEach((vehicle, index) => {
      vehicleColors[vehicle] = colors[index % colors.length];
    });

    return (
      <LineGraph
        data={tripTimelineData}
        title={`${t(chartConfig.title)} - Average Duration (Minutes)`}
        width={chartConfig.width}
        height={chartConfig.height}
        isMultiVehicle={true}
        vehicleColors={vehicleColors}
        timeField="time"
        speedField="duration" // Changed from "count" to "duration"
        vehicleField="vehicleName"
        showDots={true}
        showLegend={false}
        yAxis="duration" // Explicitly set yAxis to duration
        dateRange={selectedDateRange} // Pass dateRange for proper labeling
      />
    );
  }

  // Special handling for trip gantt timeline chart
  if (chartConfig.id === "trip_gantt_timeline") {
    const tripGanttData = mapTripDataForGanttChart(chartState.data);

    console.log("Trip Gantt Data:", tripGanttData);

    if (!tripGanttData || tripGanttData.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            No trip duration data to display
          </Typography>
        </Box>
      );
    }

    return (
      <HorizontalBarTimeline
        data={tripGanttData}
        title={`${t(chartConfig.title)}`}
        width="100%"
        height={chartConfig.height}
      />
    );
  }

  const transformedData = mapReportDataToChartData(
    chartState.data,
    chartConfig,
    vehiclesData
  );

  if (!transformedData || transformedData.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="text.secondary">
          No data to display for {t(chartConfig.title)}
        </Typography>
      </Box>
    );
  }

  const {
    chartType = "barGraph",
    title = t("Untitled Chart"),
    color = "#8884d8",
    width = 400,
    height = 300,
    id,
    barSize = 14,
  } = chartConfig;

  let displayTitle;
  if (id === "total_runtime") {
    const totalHours =
      Math.round(
        transformedData.reduce((sum, item) => sum + (item.hours || 0), 0) * 100
      ) / 100;
    displayTitle = `${t(title)}: ${totalHours} hrs`;
  } else if (id === "average_speed") {
    const totalAvg = transformedData.reduce(
      (sum, item) => sum + (item.speed || 0),
      0
    );
    const avgSpeed =
      Math.round((totalAvg / transformedData.length) * 100) / 100;
    displayTitle = `${t(title)}: ${avgSpeed} km/h`;
  } else {
    const total = calculateTotalForChart(transformedData, id);
    displayTitle = total !== null ? `${t(title)}: ${total}` : t(title);
  }
  const commonProps = {
    data: transformedData,
    title: displayTitle,
    width,
    height,
    color,
  };

  // Handle specific chart types
  if (id === "dead_halt_count") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="vehicle"
        yAxis="dead"
        xAxisLabel="Vehicles"
        yAxisLabel="No. of Halts"
        showLegend={false}
        barSize={barSize}
      />
    );
  }

  if (id === "inactive_halt_count") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="vehicle"
        yAxis="inactive"
        xAxisLabel="Vehicles"
        yAxisLabel="No. of Halts"
        showLegend={false}
        barSize={barSize}
      />
    );
  }

  if (id === "dead_halt_duration") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="vehicle"
        yAxis="dead"
        xAxisLabel="Vehicles"
        yAxisLabel="Hours"
        showLegend={false}
        barSize={barSize}
      />
    );
  }

  if (id === "inactive_halt_duration") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="vehicle"
        yAxis="inactive"
        xAxisLabel="Vehicles"
        yAxisLabel="Hours"
        showLegend={false}
        barSize={barSize}
      />
    );
  }

  if (id === "total_halt") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="vehicle"
        xAxisLabel="Vehicles"
        yAxisLabel="No. of Halts"
        showLegend={true}
        inactiveKey="inactive"
        deadKey="dead"
        inactiveColor="#f9e719"
        deadColor="#ee3b2b"
        barSize={barSize}
      />
    );
  }

  if (id === "halt_duration") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="vehicle"
        xAxisLabel="Vehicles"
        yAxisLabel="Halts Hours"
        showLegend={true}
        inactiveKey="inactive"
        deadKey="dead"
        inactiveColor="#f9e719"
        deadColor="#ee3b2b"
        barSize={barSize}
      />
    );
  }
  if (id === "total_trips") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="vehicle"
        xAxisLabel="Vehicles"
        yAxisLabel="No. of Trips"
        showLegend={true}
        completedKey="onTime"
        delayedKey="delayed"
        completedColor="#28a745"
        delayedColor="#ffc107"
        barSize={barSize}
      />
    );
  }

  // Default chart rendering logic
  const sampleDataPoint = transformedData[0];
  const keys = Object.keys(sampleDataPoint);

  let xAxis = keys[0];
  let yAxis = keys[1];
  if (id === "maximum_speed") {
    const maxSpeed = Math.max(
      ...transformedData.map((item) => item.speed || 0)
    );
    const displayTitle = `${t(title)}: ${maxSpeed} km/h`;

    return (
      <BarGraph
        {...commonProps}
        title={displayTitle}
        xAxis="vehicle"
        yAxis="speed"
        xAxisLabel={getAxisLabels(id, xAxis, yAxis).xAxisLabel}
        yAxisLabel={getAxisLabels(id, xAxis, yAxis).yAxisLabel}
        showReferenceLine={true}
        referenceLineY={maxSpeedLimit}
        referenceLineColor="red"
      />
    );
  }

  if (id === "overspeed_incidents") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="vehicle"
        yAxis="count"
        xAxisLabel={getAxisLabels(id, xAxis, yAxis).xAxisLabel}
        yAxisLabel={getAxisLabels(id, xAxis, yAxis).yAxisLabel}
      />
    );
  }

  if (id === "total_runtime") {
    xAxis = "vehicle";
    yAxis = "hours";
  } else if (id === "total_route_deviation") {
    const total = transformedData.reduce(
      (sum, item) => sum + (item.deviations || 0),
      0
    );
    const displayTitle = `${t(title)}: ${total}`;

    // Explicitly format data for PieChart
    const pieData = transformedData.map((item) => ({
      name: item.vehicle,
      value: item.deviations,
      count: item.deviations,
    }));

    return (
      <PieChart
        {...commonProps}
        data={pieData} // Use explicitly formatted data
        title={displayTitle}
        showLabels={true}
        showLegend={true}
        dataLabel="Route deviation incidents by vehicle"
        showDataDescription={true}
      />
    );
  } else if (chartType === "pieChart" && id === "trip_status") {
    const statusColors = {
      "On Time": "#28a745", // Green for on time
      Delayed: "#ffc107", // Red for delayed
      "No Trips": "#6c757d", // Gray for no trips
      Unknown: "#6f42c1",
    };

    const dataWithColors = transformedData.map((item) => ({
      ...item,
      color: statusColors[item.status] || statusColors["Unknown"],
    }));

    return (
      <PieChart
        {...commonProps}
        data={dataWithColors}
        showLabels={true}
        showLegend={true}
        dataLabel="Trip completion status distribution"
        showDataDescription={true}
      />
    );
  } else if (id === "trips_by_time_of_day") {
    // The data now contains both startCount and endCount
    if (!transformedData || transformedData.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            No trip timeline data to display
          </Typography>
        </Box>
      );
    }

    // Convert data for dual-line display
    const dualLineData = transformedData.map((item) => ({
      time: item.time,
      "Trips Start Time": item.startCount || 0,
      "Trips End Time": item.endCount || 0,
    }));

    const lineColors = {
      "Trips Start Time": "#2980B9", // Blue for start times
      "Trips End Time": "#E74C3C", // Red for end times
    };

    return (
      <LineGraph
        data={dualLineData}
        title={`${t(chartConfig.title)}`}
        width={chartConfig.width}
        height={chartConfig.height}
        isMultiVehicle={true}
        vehicleColors={lineColors}
        timeField="time"
        speedField="count"
        vehicleField="lineType"
        showDots={true}
        showLegend={false}
        xAxis="time"
        yAxis="count"
      />
    );
  } else if (id === "trip_start_end") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="period"
        xAxisLabel="Time Period"
        yAxisLabel="No. of Trips"
        showLegend={true}
        completedKey="tripStarts"
        delayedKey="tripEnds"
        completedColor="#2980B9" 
        delayedColor="#E74C3C"
        barSize={barSize}
      />
    );
  } else if (id === "stops_visited") {
    xAxis = "vehicle";
    yAxis = "stops";
  } else if (id === "trips_per_route") {
    return (
      <BarGraph
        {...commonProps}
        xAxis="route"
        xAxisLabel="Routes"
        yAxisLabel="No. of Trips"
        showLegend={true}
        completedKey="onTime"
        delayedKey="delayed"
        completedColor="#28a745"
        delayedColor="#ffc107"
        barSize={barSize}
      />
    );
  } else if (id === "distance_covered") {
    xAxis = "vehicle";
    yAxis = "distance";
  }

  const props = { ...commonProps, xAxis, yAxis };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Add loading indicator when fetching more data */}
      {loadingState?.isLoadingMore && <LoadingIndicator />}

      {/* Existing chart rendering code */}
      {(() => {
        switch (chartType) {
          case "barGraph":
            return (
              <BarGraph
                {...props}
                xAxisLabel={getAxisLabels(id, xAxis, yAxis).xAxisLabel}
                yAxisLabel={getAxisLabels(id, xAxis, yAxis).yAxisLabel}
              />
            );
          case "lineGraph":
            return <LineGraph {...props} />;
          case "pieChart":
            return (
              <PieChart
                {...props}
                dataLabel={`${yAxis} distribution by ${xAxis}`}
                showDataDescription={true}
              />
            );
          default:
            return (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography color="warning.main">
                  {t("Chart type not supported")}: {chartType}
                </Typography>
              </Box>
            );
        }
      })()}
    </Box>
  );
};

export default ChartRenderer;
